import React from "react";
import { twMerge } from "tailwind-merge";

/**
 * Grid Component — Flexible layout system for dashboards
 *
 * A higher-level abstraction over CSS Grid that provides responsive columns,
 * consistent spacing, and span helpers for building complex dashboard layouts.
 *
 * Usage:
 * ```tsx
 * <Grid cols={{ base: 1, md: 2, lg: 4 }} gap="md">
 *   <Grid.Item span={{ base: 1, lg: 2 }}>
 *     <Card>Wide content</Card>
 *   </Grid.Item>
 *   <Grid.Item>
 *     <Card>Regular content</Card>
 *   </Grid.Item>
 * </Grid>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type ResponsiveValue<T> =
  | T
  | {
      base?: T;
      sm?: T;
      md?: T;
      lg?: T;
      xl?: T;
      "2xl"?: T;
    };

export type GridCols = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | "none";
export type GridSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | "full" | "auto";
export type GridGap = "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
export type GridAlign = "start" | "center" | "end" | "stretch" | "baseline";
export type GridJustify = "start" | "center" | "end" | "between" | "around" | "evenly";
export type GridFlow = "row" | "col" | "dense" | "row-dense" | "col-dense";

export interface GridProps {
  /** Number of columns at different breakpoints */
  cols?: ResponsiveValue<GridCols>;
  /** Gap between grid items using design tokens */
  gap?: GridGap;
  /** Gap between rows specifically */
  gapY?: GridGap;
  /** Gap between columns specifically */
  gapX?: GridGap;
  /** Vertical alignment of items */
  align?: GridAlign;
  /** Horizontal justification of the grid */
  justify?: GridJustify;
  /** Grid flow direction */
  flow?: GridFlow;
  /** Auto-fit mode with minimum item width */
  autoFit?: string; // e.g., "280px"
  /** Auto-fill mode with minimum item width */
  autoFill?: string; // e.g., "280px"
  /** Additional CSS classes */
  className?: string;
  /** Child elements */
  children?: React.ReactNode;
}

export interface GridItemProps {
  /** Column span at different breakpoints */
  span?: ResponsiveValue<GridSpan>;
  /** Column start position */
  colStart?: ResponsiveValue<number | "auto">;
  /** Column end position */
  colEnd?: ResponsiveValue<number | "auto">;
  /** Row span at different breakpoints */
  rowSpan?: ResponsiveValue<GridSpan>;
  /** Row start position */
  rowStart?: ResponsiveValue<number | "auto">;
  /** Row end position */
  rowEnd?: ResponsiveValue<number | "auto">;
  /** Additional CSS classes */
  className?: string;
  /** Child elements */
  children?: React.ReactNode;
}

// ——————————————————————————————————————————
// Style Mappings
// ——————————————————————————————————————————

const colsMap: Record<GridCols, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
  7: "grid-cols-7",
  8: "grid-cols-8",
  9: "grid-cols-9",
  10: "grid-cols-10",
  11: "grid-cols-11",
  12: "grid-cols-12",
  none: "grid-cols-none",
};

const responsiveColsMap: Record<string, Record<GridCols, string>> = {
  sm: {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
    4: "sm:grid-cols-4",
    5: "sm:grid-cols-5",
    6: "sm:grid-cols-6",
    7: "sm:grid-cols-7",
    8: "sm:grid-cols-8",
    9: "sm:grid-cols-9",
    10: "sm:grid-cols-10",
    11: "sm:grid-cols-11",
    12: "sm:grid-cols-12",
    none: "sm:grid-cols-none",
  },
  md: {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
    6: "md:grid-cols-6",
    7: "md:grid-cols-7",
    8: "md:grid-cols-8",
    9: "md:grid-cols-9",
    10: "md:grid-cols-10",
    11: "md:grid-cols-11",
    12: "md:grid-cols-12",
    none: "md:grid-cols-none",
  },
  lg: {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
    7: "lg:grid-cols-7",
    8: "lg:grid-cols-8",
    9: "lg:grid-cols-9",
    10: "lg:grid-cols-10",
    11: "lg:grid-cols-11",
    12: "lg:grid-cols-12",
    none: "lg:grid-cols-none",
  },
  xl: {
    1: "xl:grid-cols-1",
    2: "xl:grid-cols-2",
    3: "xl:grid-cols-3",
    4: "xl:grid-cols-4",
    5: "xl:grid-cols-5",
    6: "xl:grid-cols-6",
    7: "xl:grid-cols-7",
    8: "xl:grid-cols-8",
    9: "xl:grid-cols-9",
    10: "xl:grid-cols-10",
    11: "xl:grid-cols-11",
    12: "xl:grid-cols-12",
    none: "xl:grid-cols-none",
  },
  "2xl": {
    1: "2xl:grid-cols-1",
    2: "2xl:grid-cols-2",
    3: "2xl:grid-cols-3",
    4: "2xl:grid-cols-4",
    5: "2xl:grid-cols-5",
    6: "2xl:grid-cols-6",
    7: "2xl:grid-cols-7",
    8: "2xl:grid-cols-8",
    9: "2xl:grid-cols-9",
    10: "2xl:grid-cols-10",
    11: "2xl:grid-cols-11",
    12: "2xl:grid-cols-12",
    none: "2xl:grid-cols-none",
  },
};

const gapMap: Record<GridGap, string> = {
  none: "gap-0",
  xs: "gap-x-2 gap-y-1", // 8px horizontal, 4px vertical
  sm: "gap-x-3 gap-y-2", // 12px horizontal, 8px vertical
  md: "gap-x-4 gap-y-3", // 16px horizontal, 12px vertical
  lg: "gap-x-6 gap-y-4", // 24px horizontal, 16px vertical
  xl: "gap-x-8 gap-y-6", // 32px horizontal, 24px vertical
  "2xl": "gap-x-12 gap-y-8", // 48px horizontal, 32px vertical
};

const gapXMap: Record<GridGap, string> = {
  none: "gap-x-0",
  xs: "gap-x-2",
  sm: "gap-x-3",
  md: "gap-x-4",
  lg: "gap-x-6",
  xl: "gap-x-8",
  "2xl": "gap-x-12",
};

const gapYMap: Record<GridGap, string> = {
  none: "gap-y-0",
  xs: "gap-y-1", // 4px (reduced from 8px)
  sm: "gap-y-2", // 8px (reduced from 12px)
  md: "gap-y-3", // 12px (reduced from 16px)
  lg: "gap-y-4", // 16px (reduced from 24px)
  xl: "gap-y-6", // 24px (reduced from 32px)
  "2xl": "gap-y-8", // 32px (reduced from 48px)
};

const alignMap: Record<GridAlign, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

const justifyMap: Record<GridJustify, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evenly",
};

const flowMap: Record<GridFlow, string> = {
  row: "grid-flow-row",
  col: "grid-flow-col",
  dense: "grid-flow-dense",
  "row-dense": "grid-flow-row-dense",
  "col-dense": "grid-flow-col-dense",
};

const spanMap: Record<GridSpan, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
  10: "col-span-10",
  11: "col-span-11",
  12: "col-span-12",
  full: "col-span-full",
  auto: "col-auto",
};

const responsiveSpanMap: Record<string, Record<GridSpan, string>> = {
  sm: {
    1: "sm:col-span-1",
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    7: "sm:col-span-7",
    8: "sm:col-span-8",
    9: "sm:col-span-9",
    10: "sm:col-span-10",
    11: "sm:col-span-11",
    12: "sm:col-span-12",
    full: "sm:col-span-full",
    auto: "sm:col-auto",
  },
  md: {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    5: "md:col-span-5",
    6: "md:col-span-6",
    7: "md:col-span-7",
    8: "md:col-span-8",
    9: "md:col-span-9",
    10: "md:col-span-10",
    11: "md:col-span-11",
    12: "md:col-span-12",
    full: "md:col-span-full",
    auto: "md:col-auto",
  },
  lg: {
    1: "lg:col-span-1",
    2: "lg:col-span-2",
    3: "lg:col-span-3",
    4: "lg:col-span-4",
    5: "lg:col-span-5",
    6: "lg:col-span-6",
    7: "lg:col-span-7",
    8: "lg:col-span-8",
    9: "lg:col-span-9",
    10: "lg:col-span-10",
    11: "lg:col-span-11",
    12: "lg:col-span-12",
    full: "lg:col-span-full",
    auto: "lg:col-auto",
  },
  xl: {
    1: "xl:col-span-1",
    2: "xl:col-span-2",
    3: "xl:col-span-3",
    4: "xl:col-span-4",
    5: "xl:col-span-5",
    6: "xl:col-span-6",
    7: "xl:col-span-7",
    8: "xl:col-span-8",
    9: "xl:col-span-9",
    10: "xl:col-span-10",
    11: "xl:col-span-11",
    12: "xl:col-span-12",
    full: "xl:col-span-full",
    auto: "xl:col-auto",
  },
  "2xl": {
    1: "2xl:col-span-1",
    2: "2xl:col-span-2",
    3: "2xl:col-span-3",
    4: "2xl:col-span-4",
    5: "2xl:col-span-5",
    6: "2xl:col-span-6",
    7: "2xl:col-span-7",
    8: "2xl:col-span-8",
    9: "2xl:col-span-9",
    10: "2xl:col-span-10",
    11: "2xl:col-span-11",
    12: "2xl:col-span-12",
    full: "2xl:col-span-full",
    auto: "2xl:col-auto",
  },
};

// ——————————————————————————————————————————
// Helper Functions
// ——————————————————————————————————————————

function getResponsiveClasses<T extends string | number>(
  value: ResponsiveValue<T> | undefined,
  baseMap: Record<T, string>,
  responsiveMap: Record<string, Record<T, string>>
): string {
  if (!value) return "";

  // Simple value
  if (typeof value !== "object") {
    return baseMap[value as T] || "";
  }

  // Responsive object
  const classes: string[] = [];

  if ("base" in value && value.base !== undefined) {
    classes.push(baseMap[value.base]);
  }

  Object.entries(responsiveMap).forEach(([breakpoint, map]) => {
    const breakpointValue = (value as Record<string, T | undefined>)[breakpoint];
    if (breakpointValue !== undefined) {
      classes.push(map[breakpointValue as T]);
    }
  });

  return classes.join(" ");
}

// ——————————————————————————————————————————
// Grid Component
// ——————————————————————————————————————————

export function Grid({
  cols = 1,
  gap = "md",
  gapX,
  gapY,
  align = "stretch",
  justify = "start",
  flow = "row",
  autoFit,
  autoFill,
  className,
  children,
}: GridProps) {
  // Build grid classes
  const gridClasses = twMerge(
    "grid",
    // Handle auto-fit/auto-fill or responsive columns
    autoFit || autoFill ? "" : getResponsiveClasses(cols, colsMap, responsiveColsMap),
    // Gap classes
    !gapX && !gapY && gapMap[gap],
    gapX && gapXMap[gapX],
    gapY && gapYMap[gapY],
    // Alignment
    alignMap[align],
    justifyMap[justify],
    // Flow
    flowMap[flow],
    className
  );

  // Handle auto-fit/auto-fill with inline styles
  const style: React.CSSProperties = {};
  if (autoFit) {
    style.gridTemplateColumns = `repeat(auto-fit, minmax(${autoFit}, 1fr))`;
  } else if (autoFill) {
    style.gridTemplateColumns = `repeat(auto-fill, minmax(${autoFill}, 1fr))`;
  }

  return (
    <div className={gridClasses} style={style}>
      {children}
    </div>
  );
}

// ——————————————————————————————————————————
// Grid.Item Component
// ——————————————————————————————————————————

export function GridItem({
  span,
  // colStart,
  // colEnd,
  // rowSpan,
  // rowStart,
  // rowEnd,
  className,
  children,
}: GridItemProps) {
  const itemClasses = twMerge(
    // Column span
    getResponsiveClasses(span, spanMap, responsiveSpanMap),
    // Additional positioning classes would go here
    // For brevity, not implementing all start/end mappings
    className
  );

  return <div className={itemClasses}>{children}</div>;
}

// ——————————————————————————————————————————
// Compound API
// ——————————————————————————————————————————

Grid.Item = GridItem;
